import {
    Avatar,
    Box,
    Button,
    Card,
    List,
    ListItem,
    ListItemAvatar,
    ListItemSecondaryAction,
    ListItemText,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import StripedDataGrid from '../../../shared/StripedDataGrid';
import { GridColDef } from '@mui/x-data-grid';
import { Add } from '@mui/icons-material';
import ContractorInviteForm from './ContractorInviteForm';
import {
    useCancelContractorInviteMutation,
    useGetContractorInvitesQuery,
    useGetOwnerContractorsQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../shared/AuthRoles';
import toast from '../../../shared/Toast';
import { camelCaseToTitleCase } from '../../../../shared/utils';
import ContractorModal from './ContractorModal';
import { Permissions } from '../../../enums/contractor';
import { State } from '../../../types/generic';
import { CustomChip } from '../../../../layout/styledcomponents/CustomChip';
import { CustomIconButton } from '../../../../layout/styledcomponents/CustomIconButton';
import { useLocation, useNavigate } from 'react-router';

const ContractorList = () => {
    const loggedInUser = UserRole();
    const navigate = useNavigate();
    const location = useLocation();

    const { data: contractors } = useGetOwnerContractorsQuery(
        loggedInUser.company,
        { skip: !loggedInUser?.company },
    );

    const isMobile = useMediaQuery('(max-width: 770px)');

    const { data: invites } = useGetContractorInvitesQuery();
    const [cancelInvite] = useCancelContractorInviteMutation();

    const [inviteFormOpen, setInviteFormOpen] = useState(false);
    const [contractorOpen, setContractorOpen] = useState<number | null>(null);

    useEffect(() => {
        const state: any = location.state;
        const searchParams = new URLSearchParams(location.search);
        const fromQuery = searchParams.get('openCreateContractor') === 'true';

        if (state?.openCreateContractor || fromQuery) {
            setInviteFormOpen(true);
            navigate(location.pathname, { replace: true, state: null as any });
        }
    }, [location.state, location.search, navigate, location.pathname]);

    const columns: GridColDef[] = [
        {
            field: 'name',
            headerName: 'Contractor',
            flex: 1,
            renderCell: (params) => {
                return (
                    <ListItem disableGutters disablePadding>
                        <ListItemAvatar>
                            <Avatar src={params.row.profilePicture?.src} />
                        </ListItemAvatar>
                        <ListItemText
                            primary={params.value}
                            secondary={`ABN: ${params.row.abn}`}
                        />
                    </ListItem>
                );
            },
        },
        {
            field: 'primaryContact',
            headerName: 'Primary Contact',
            flex: 0.5,
        },
        {
            field: 'email',
            headerName: 'Email',
            flex: 0.5,
        },
        {
            field: 'phone',
            headerName: 'Phone Number',
            flex: 0.5,
        },
        {
            field: 'serviceStates',
            headerName: 'Service Locations',
            flex: 1,
            valueGetter: (param) =>
                param.value.map(({ state }: { state: State }) => state),
            renderCell: (params) => {
                const remainders = isMobile ? params.value.length : 0;
                return (
                    <Box sx={{ display: 'flex', overflow: 'clip', gap: 0.5 }}>
                        {!isMobile &&
                            params.value
                                ?.slice(0, 2)
                                .map((state: State, index: number) => (
                                    <CustomChip
                                        size='small'
                                        key={index}
                                        label={camelCaseToTitleCase(state)}
                                    />
                                ))}
                        {remainders > 0 && (
                            <CustomChip
                                size='small'
                                variant='outlined'
                                label={`+ ${remainders}`}
                            />
                        )}
                    </Box>
                );
            },
        },
        {
            field: 'permissions',
            headerName: 'Permissions',
            flex: 0.5,
            renderCell: (params) => {
                return (
                    <CustomChip
                        variant='outlined'
                        color={
                            params.row.permissions === Permissions.Disabled
                                ? 'red'
                                : 'green'
                        }
                        label={camelCaseToTitleCase(
                            params.row.permissions,
                        ).toUpperCase()}
                    />
                );
            },
        },
    ];

    const handleCancel = async (id: number) => {
        try {
            await cancelInvite(id).unwrap();
            toast.success('Invitation cancelled successfully');
        } catch {
            toast.error('Error cancelling invitation');
        }
    };

    const handleOpen = (id: number) => {
        navigate(`/contractors/${id}`);
    };

    return (
        <>
            <Stack sx={{ pt: '30px', gap: 4 }}>
                <div>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography variant='subHeading'>
                            Pending Invites
                        </Typography>
                        <Button
                            variant='contained'
                            startIcon={<Add />}
                            sx={{ my: 1 }}
                            onClick={() => setInviteFormOpen(true)}
                        >
                            Invite
                        </Button>
                    </Box>
                    {invites && invites.length > 0 ? (
                        <List disablePadding>
                            {invites.map((invite: any) => (
                                <ListItem
                                    key={invite.id}
                                    divider
                                    disableGutters
                                >
                                    <ListItemText
                                        primary={`${
                                            invite.contractor.name
                                        } - invite sent ${new Date(
                                            invite.sentAt,
                                        ).toLocaleString([], {
                                            day: '2-digit',
                                            month: 'long',
                                            hour12: true,
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        })}`}
                                        secondary={invite.contractor.email}
                                    />

                                    <ListItemSecondaryAction>
                                        <CustomIconButton
                                            variant='contained'
                                            onClick={() =>
                                                handleCancel(invite.id)
                                            }
                                            sx={{
                                                '&:hover': {
                                                    cursor: 'pointer',
                                                    color: 'red',
                                                    backgroundColor:
                                                        'transparent',
                                                    background: 'none',
                                                },
                                            }}
                                        >
                                            <DeleteOutline
                                                sx={{ fontSize: '16px' }}
                                            />
                                        </CustomIconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <Card variant='outlined'>
                            <Box
                                sx={{
                                    p: 4,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                No pending invites.
                            </Box>
                        </Card>
                    )}
                </div>
                <Typography variant='subHeading'>Active Contractors</Typography>
            </Stack>

            <StripedDataGrid
                getRowId={(row) => row.id}
                columns={columns}
                rows={contractors || []}
                onRowClick={(row) => handleOpen(row.id as number)}
                sx={{ border: 0, cursor: 'pointer' }}
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                autoHeight
            />

            <ContractorInviteForm
                open={inviteFormOpen}
                onClose={() => setInviteFormOpen(false)}
            />

            {contractorOpen && (
                <ContractorModal
                    id={contractorOpen}
                    open={!!contractorOpen}
                    onClose={() => setContractorOpen(null)}
                />
            )}
        </>
    );
};

export default ContractorList;
